/**
 * getHeroCtaSlackExtra
 *
 * Devuelve un flat dict listo para spreadear en el `extra` del POST a
 * /api/track-funnel-failure (y endpoints similares que terminan en Slack).
 *
 * Lo que aporta al webhook:
 *   - hero_cta_type      → "wishlist" | "community" | "instagram" | "whatsapp" |
 *                          "birthday" | "dual" | "none" | ""
 *   - hero_cta_label     → texto exacto del botón que vio el visitor
 *   - hero_cta_url       → URL destino (admin puede verificar que fue el correcto)
 *   - hero_cta_emoji     → emoji del CTA (si aplica)
 *   - hero_cta_resolution_source → "kv-hit" | "kv-miss-vercel" | "db-vercel" |
 *                                  "edge" (diagnostic del CTA resolver)
 *   - worker_template    → "BaseTemplate" | "VideoTemplate" |
 *                          "BioTemplatePremium" | "DualCTAVideoLanding" | etc.
 *   - geo_summary        → "AR · Buenos Aires · Tigre" (one-liner para el Slack)
 *
 * Llamada cero costo: sólo lee del window.__TEMPLATE_CONFIG__ inyectado por el
 * Cloudflare Worker. No hace fetch ni cálculos costosos.
 *
 * Use:
 *   import { getHeroCtaSlackExtra } from "@/lib/heroCtaSlackContext";
 *   ...
 *   void fetch("/api/track-funnel-failure", {
 *     method: "POST",
 *     body: JSON.stringify({
 *       kind: "...",
 *       template: "...",
 *       extra: {
 *         ...getHeroCtaSlackExtra(),
 *         // resto de extras específicos del evento
 *       },
 *     }),
 *   });
 */

import { getFirstVisitAttribution } from "@/lib/firstVisitAttribution";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type HeroCtaSlackExtra = {
  hero_cta_type?: string;
  hero_cta_label?: string;
  hero_cta_url?: string;
  hero_cta_emoji?: string;
  hero_cta_resolution_source?: string;
  worker_template?: string;
  geo_summary?: string;
  /** Attribution del primer pageview — referrer + landing (con UTM) crudos. */
  referrer?: string;
  landingUrl?: string;
};

const CAP_LABEL = 120;
const CAP_URL = 200;
const CAP_EMOJI = 12;
const CAP_GEO_SUMMARY = 160;

function decodeMaybe(raw: string | null | undefined): string | undefined {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  try {
    return decodeURIComponent(s.replace(/\+/g, " "));
  } catch {
    return s;
  }
}

export function getHeroCtaSlackExtra(): HeroCtaSlackExtra {
  if (typeof window === "undefined") return {};
  const cfg = getServerTemplateConfig();
  if (!cfg) return {};

  const out: HeroCtaSlackExtra = {};
  if (cfg.ctaType) out.hero_cta_type = cfg.ctaType;
  if (cfg.ctaLabel) out.hero_cta_label = cfg.ctaLabel.slice(0, CAP_LABEL);
  if (cfg.ctaUrl) out.hero_cta_url = cfg.ctaUrl.slice(0, CAP_URL);
  if (cfg.ctaEmoji) out.hero_cta_emoji = cfg.ctaEmoji.slice(0, CAP_EMOJI);
  if (cfg.ctaResolutionSource) out.hero_cta_resolution_source = cfg.ctaResolutionSource;
  if (cfg.templateName) out.worker_template = cfg.templateName;

  // Geo summary one-liner para el Slack: "AR · Buenos Aires · Tigre".
  // Reusa la lógica de serverTemplateDetection.getWorkerGeoAnalyticsProps
  // pero acá lo aplanamos directo para no acoplar más.
  const country =
    cfg.country && cfg.country !== "UNKNOWN" ? cfg.country : undefined;
  const province = cfg.province?.trim() || undefined;
  const city = decodeMaybe(cfg.city);
  const parts = [country, province, city].filter(
    (x): x is string => typeof x === "string" && x.length > 0,
  );
  if (parts.length > 0) {
    out.geo_summary = parts.join(" · ").slice(0, CAP_GEO_SUMMARY);
  }

  // Attribution del primer pageview — referrer + landing URL con UTM crudos.
  // Sobrevive nav SPA via sessionStorage (recordFirstVisitAttribution en boot).
  // El backend parsea esto en sendMembershipCheckoutAlert + cron Slack para
  // mostrar la fuente real de cada conversión (IG bio / Google search / DM).
  const attribution = getFirstVisitAttribution();
  if (attribution.referrer) out.referrer = attribution.referrer.slice(0, 1000);
  if (attribution.landingUrl) out.landingUrl = attribution.landingUrl.slice(0, 1000);

  return out;
}
